import { useMemo } from 'react';
import { useCanDeactivate } from '@app/shared/hooks/useCanDeactivate';
import { CreatePipelineStepPaths } from './create-pipeline-state-machine/interfaces/create-pipeline-step-paths';
import { useCreatePipelineWizard } from './hooks/useCreatePipelineWizard';
import { useCreatePipelineContext } from './hooks/useCreatePipelineContext';
import { getCreatePipelineWizardStepsWithExtraProps } from './utils/createPipelineWizardUtil';
import { Wizard } from '../../wizard/Wizard/Wizard';
import { CreatePipelineWizardHeaderLeft } from './components/CreatePipelineWizardHeaderLeft';
import { useConfirm } from '../../modal/ConfirmModal/useConfirm';
import { DiscardChangesConfirmModalBody } from '../../shared/components/DiscardChangesConfirmModalBody/DiscardChangesConfirmModalBody';

export function CreatePipelineWizard({
  isCommonSourceTypeFlow
}: {
  isCommonSourceTypeFlow: boolean;
}) {
  const { pipeline, sourceType, source, destination, destinationType } = useCreatePipelineContext();
  const { confirm } = useConfirm();

  const {
    wizardState,
    currentStep,
    stepGroups,
    stepTreeSnapshot,
    currentStepFooterProps,
    wizardCompleted,
    onCancelClick,
    onBackClick,
    selectStepGroup
  } = useCreatePipelineWizard({
    integration: pipeline!,
    isCommonSourceTypeFlow
  });

  const stepGroupsWithExtra = useMemo(() => {
    return getCreatePipelineWizardStepsWithExtraProps({
      source,
      destination,
      stepGroups,
      wizardCurrentStep: currentStep,
      sourceType,
      destinationType
    });
  }, [source, destination, stepGroups, currentStep, sourceType, destinationType]);

  const cancel = () => {
    onCancelClick();
  };

  useCanDeactivate(
    () =>
      !wizardCompleted
        ? confirm({
            title: 'Discard Changes',
            negativeButtonText: "Don't Discard",
            positiveButtonText: 'Discard',
            BodyComponent: DiscardChangesConfirmModalBody
          }).then(confirmed => confirmed as boolean)
        : true,
    () => !!wizardCompleted,
    tx => {
      const wizardStep = new URLSearchParams(tx.location.search).get('wizard_step');
      return (
        isCommonSourceTypeFlow &&
        (!wizardStep || wizardStep === CreatePipelineStepPaths.SELECT_SOURCE_TYPE)
      );
    }
  );

  return (
    <Wizard
      wizardState={wizardState}
      currentStep={currentStep}
      stepGroups={stepGroupsWithExtra}
      stepTreeSnapshot={stepTreeSnapshot}
      currentStepFooterProps={currentStepFooterProps}
      onBackClick={onBackClick}
      onCancelClick={cancel}
      selectStepGroup={selectStepGroup}
      WizardHeaderLeftComponent={CreatePipelineWizardHeaderLeft}
      wizardHeaderLeftComponentProps={{ pipelineName: pipeline?.name }}
      trackingProps={{
        wizard: 'PIPELINE_CREATE'
      }}
    />
  );
}
